import Element from './Element';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import mixin from '../privates/utils/mixin';
import * as members from '../privates/privateMembers';
import {validate} from '../privates/validations/validation';
import backgroundMediaUtils from '../privates/utils/backgroundMediaUtils';

/*
 * @class VideoBox
 * @summary Lets you display your videos with creative frames and graphic overlays.
 * @mixes $w.Element
 * @mixes $w.HiddenCollapsedMixin
 * @memberof $w
 * @queryable
 * @tagname videoBox
 * @viewername wysiwyg.viewer.components.MediaPlayer
 * @definitionId wysiwyg.viewer.components.MediaPlayer
 */

class VideoBox extends Element {
    get isPlaying() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const {isPlaying = false} = RMI.getState(compId);
        
        return isPlaying;
    }
    
    get currentTime() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const {currentTime = 0} = RMI.getState(compId);    
        
        return currentTime;
    }
    
    get duration() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const {duration} = RMI.getState(compId);
    
        return duration;
    }
    
    get volume() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const {volume = 0} = RMI.getState(compId);    
        
        return volume * 100;
    }
    
    /**
    * @member volume
    * @memberof $w.VideoPlayer
    * @param {external:Number} volume
    */
    set volume(volume) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compType = members._type.get(this).compType;
    
        if (!validate({
            compType,
            functionName: 'volume',
            dataToValidate: {volume},
            compName: this.id
        })) {
            return;
        }
    
        RMI.executeCompBehavior(compId, 'setVolume', {volume: volume / 100});
    }
    
    get isMuted() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const {isMuted = false} = RMI.getState(compId);
    
        return isMuted;
    }

    play() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
    
        return new Promise((resolve) => {
            RMI.executeCompBehavior(compId, 'play', {}, () => resolve());
        });
    }
    
    pause() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
    
        return new Promise((resolve) => {
            RMI.executeCompBehavior(compId, 'pause', {}, () => resolve());
        });
    }

    stop() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
    
        return new Promise((resolve) => {
            RMI.executeCompBehavior(compId, 'stop', {}, () => resolve());
        });
    }

    togglePlay() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
    
        return new Promise((resolve) => {
            RMI.executeCompBehavior(compId, 'togglePlay', {}, () => resolve());
        });
    }

    mute() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
    
        return new Promise((resolve) => {
            RMI.executeCompBehavior(compId, 'mute', {}, () => resolve());
        });
    }

    unmute() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
    
        return new Promise((resolve) => {
            RMI.executeCompBehavior(compId, 'unMute', {}, () => resolve());
        });
    }

    seek(time) {
        const RMI = members._RMI.get(this);
        const compType = members._type.get(this).compType;
        const compId = members._compId.get(this);

        if (!validate({
            compType,
            functionName: 'seek',
            dataToValidate: {seek: time},
            compName: this.id
        })) {
            return;
        }
        
        return new Promise((resolve) => {
            RMI.executeCompBehavior(compId, 'seek', {time}, () => resolve());
        });
    }
    
    /**
     * 
     * @param cb 
     * @eventtype onPlay
     * @eventhandler
     */
    onPlay(cb) {
        const RMI = members._RMI.get(this);
        const compType = members._type.get(this).compType;

        if (!validate({
            compType,
            functionName: 'onPlay',
            dataToValidate: {onPlay: cb},
            compName: this.id
        })) {
            return this;
        }

        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);

        RMI.registerEvent(contextId, compId, RMI.getEventTypes().ON_PLAY, controllerId, cb);
        return this;
    }

    /**
     * 
     * @param cb 
     * @eventtype onPause
     * @eventhandler
     */
    onPause(cb) {
        const RMI = members._RMI.get(this);
        const compType = members._type.get(this).compType;

        if (!validate({
            compType,
            functionName: 'onPause',
            dataToValidate: {onPause: cb},
            compName: this.id
        })) {
            return this;
        }

        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);

        RMI.registerEvent(contextId, compId, RMI.getEventTypes().ON_PAUSE, controllerId, cb);
        return this;
    }

    /**
     * 
     * @param cb 
     * @eventtype onEnded
     * @eventhandler
     */
    onEnded(cb) {
        const RMI = members._RMI.get(this);
        const compType = members._type.get(this).compType;

        if (!validate({
            compType,
            functionName: 'onEnded',
            dataToValidate: {onEnded: cb},
            compName: this.id
        })) {
            return this;
        }

        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);

        RMI.registerEvent(contextId, compId, RMI.getEventTypes().ON_ENDED, controllerId, cb);
        return this;
    }

    /**
     * 
     * @param cb 
     * @eventtype onProgress
     * @eventhandler
     */
    onProgress(cb) {
        const RMI = members._RMI.get(this);
        const compType = members._type.get(this).compType;

        if (!validate({
            compType,
            functionName: 'onProgress',
            dataToValidate: {onProgress: cb},
            compName: this.id
        })) {
            return this;
        }

        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);

        RMI.registerEvent(contextId, compId, RMI.getEventTypes().ON_PROGRESS, controllerId, cb);
        return this;
    }

    // get poster() {
    //     const RMI = members._RMI.get(this);
    //     const compId = members._compId.get(this);
    //     const compDesign = RMI.getDesign(compId) || {};
    //     const {mediaRef = {}} = compDesign.background;
    //     const {posterImageRef = {}} = mediaRef;
        
    //     return backgroundMediaUtils.buildFullUrlFromMediaRef(posterImageRef);
    // }

    // set poster(value) {
    //     const compType = members._type.get(this).compType;
    //     if (!validate({
    //         compType,
    //         functionName: 'poster',
    //         dataToValidate: {poster: value},
    //         compName: this.id
    //     })) {
    //         return;
    //     }

    //     const RMI = members._RMI.get(this);
    //     const compId = members._compId.get(this);
    //     const compDesign = RMI.getDesign(compId) || {};
    //     const mediaData = compDesign.background.mediaRef;
    //     const posterData = backgroundMediaUtils.getMediaDataFromSrc(value);
        
    //     if (!posterData) {
    //         return;
    //     }
    //     mediaData.posterImageRef = posterData;

    //     RMI.setDesign(compId, mergedCompDesign(compDesign, mediaData));
    // }
    
    get src() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compDesign = RMI.getDesign(compId) || {};
        const mediaRef = compDesign.background.mediaRef || {};

        return backgroundMediaUtils.buildFullUrlFromMediaRef(mediaRef);
    }

    set src(value) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'src',
            dataToValidate: {src: value},
            compName: this.id
        })) {
            return;
        }
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compDesign = RMI.getDesign(compId);
        const mediaData = backgroundMediaUtils.getMediaDataFromSrc(value);
        
        // Silently fail
        if (!mediaData || !compDesign) {
            return;
        }

        const currentMediaData = compDesign.background.mediaRef;
        const {opacity = 1} = currentMediaData;
        // Merge partial data 
        mediaData.opacity = opacity;
        Object.assign(compDesign.background.mediaRef, mediaData);

        /*setDesignAsync with full media data*/
        backgroundMediaUtils.getFullMediaData(mediaData, (fullMediaData) => {
            if (!fullMediaData){
                return;
            }
            const updatedCompDesign = RMI.getDesign(compId);
            // Preserve opacity 
            fullMediaData.opacity = updatedCompDesign.background.mediaRef.opacity;
            // set mediaFeatures to null if not present, runtimeDAL always merge and we need it not to be undefined
            fullMediaData.mediaFeatures = fullMediaData.mediaFeatures || null;
            //Replace mediaData completely
            updatedCompDesign.background.mediaRef = fullMediaData;
            RMI.setDesign(compId, updatedCompDesign);
        });

        // Set props to preserve playstate
        RMI.setProps(compId, {
            autoplay: this.isPlaying, 
            mute: this.isMuted
        });  

        // Set partial media data
        RMI.setDesign(compId, compDesign);
    }

    toJSON() {
        const {id, type, global, rendered, isPlaying, currentTime, duration, volume, isMuted} = this;
        return {id, type, global, rendered, isPlaying, currentTime, duration, volume, isMuted};
      }


}
mixin(VideoBox.prototype, HiddenCollapsedMixin.prototype);

export default VideoBox;

